/** @format */

import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  deleteUser,
  sendEmailVerification,
  User as FirebaseUser,
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, increment, getDocs as getFirestoreDocs, deleteDoc, onSnapshot, serverTimestamp, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { auth, db, storage } from "../firebase/config";
import { StudentUser, SignUpForm, ApiResponse } from "../types";
import toast from "react-hot-toast";

interface EnrolledCourse {
  course_id: string;
  enrolled_at: Date;
  expire_date: Date;
  status: string;
}

interface AuthContextType {
  currentUser: StudentUser | null;
  loading: boolean;
  signUp: (formData: SignUpForm) => Promise<ApiResponse<StudentUser>>;
  signIn: (
    email: string,
    password: string
  ) => Promise<ApiResponse<StudentUser>>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  updateProfile: (updates: Partial<StudentUser>) => Promise<void>;
  uploadProfileImage: (file: File) => Promise<string>;
  getUserEnrolledCourses: () => Promise<EnrolledCourse[]>;
  isAuthenticated: boolean;
  firebaseUser: FirebaseUser | null;
  isEmbargoed: boolean;
  sendVerification: () => Promise<ApiResponse<void>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<StudentUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEmbargoed, setIsEmbargoed] = useState(false);

  // Single-session management
  const sessionIdRef = useRef<string | null>(null);
  const isActiveOwnerRef = useRef<boolean>(false);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const userDocUnsubRef = useRef<(() => void) | null>(null);
  const hasAutoSignedOutRef = useRef<boolean>(false);
  const embargoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const SESSION_LOCAL_STORAGE_KEY = (uid: string) => `profstudy_session:${uid}`;
  const RECENT_THRESHOLD_MS = 45 * 1000; // 45 seconds window for considering another session active

  const generateSessionId = (): string => {
    const cryptoObj = (window as any)?.crypto;
    if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
      return cryptoObj.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`.toUpperCase();
  };

  const getOrCreateLocalSessionId = (uid: string): string => {
    const key = SESSION_LOCAL_STORAGE_KEY(uid);
    let localId = localStorage.getItem(key);
    if (!localId) {
      localId = generateSessionId();
      localStorage.setItem(key, localId);
    }
    return localId;
  };

  const clearLocalSessionId = (uid: string) => {
    const key = SESSION_LOCAL_STORAGE_KEY(uid);
    localStorage.removeItem(key);
  };

  const startHeartbeat = (uid: string) => {
    if (heartbeatIntervalRef.current) return;
    heartbeatIntervalRef.current = setInterval(async () => {
      try {
        // Only update heartbeat if we are the active owner (prevents keeping someone else embargoed)
        if (auth.currentUser?.uid === uid && isActiveOwnerRef.current) {
          await updateDoc(doc(db, "users", uid), {
            activeSessionUpdatedAt: serverTimestamp(),
          });
        }
      } catch (err) {
        // no-op
      }
    }, 15 * 1000);
  };

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  const subscribeToUserDoc = (uid: string) => {
    if (userDocUnsubRef.current) return;
    userDocUnsubRef.current = onSnapshot(doc(db, "users", uid), (snapshot) => {
      const data = snapshot.data() as any;
      const remoteSessionId = data?.activeSessionId as string | undefined;
      const updatedAt = data?.activeSessionUpdatedAt as Timestamp | Date | undefined;

      const localSessionId = sessionIdRef.current;
      let updatedDate: Date | null = null;
      if (updatedAt instanceof Timestamp) {
        updatedDate = updatedAt.toDate();
      } else if (updatedAt instanceof Date) {
        updatedDate = updatedAt;
      }

      const isRecent = updatedDate ? (Date.now() - updatedDate.getTime()) <= RECENT_THRESHOLD_MS : false;

      // We are owner if the remote session matches our local session ID
      const weOwn = !!localSessionId && remoteSessionId === localSessionId;
      isActiveOwnerRef.current = weOwn;

      // Embargo if a different active session is recent
      const embargo = !!remoteSessionId && !!localSessionId && remoteSessionId !== localSessionId && isRecent;
      setIsEmbargoed(embargo);

      // If embargoed, schedule auto sign-out after 10 seconds; cancel if embargo clears
      if (embargo) {
        if (!embargoTimeoutRef.current && !hasAutoSignedOutRef.current) {
          embargoTimeoutRef.current = setTimeout(() => {
            hasAutoSignedOutRef.current = true;
            signOut(auth).catch(() => {
              // ignore
            });
          }, 10000);
        }
      } else {
        if (embargoTimeoutRef.current) {
          clearTimeout(embargoTimeoutRef.current);
          embargoTimeoutRef.current = null;
        }
        // Allow future embargo events to schedule sign-out again
        hasAutoSignedOutRef.current = false;
      }
    });
  };

  const unsubscribeUserDoc = () => {
    if (userDocUnsubRef.current) {
      userDocUnsubRef.current();
      userDocUnsubRef.current = null;
    }
  };

  const initializeSingleSession = async (uid: string) => {
    // Persist or create local session id for this user (same across tabs)
    const localSessionId = getOrCreateLocalSessionId(uid);
    sessionIdRef.current = localSessionId;

    // Claim active session in Firestore
    try {
      await setDoc(
        doc(db, "users", uid),
        {
          activeSessionId: localSessionId,
          activeSessionUpdatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      // ignore
    }

    // Begin heartbeat and subscribe to remote changes
    startHeartbeat(uid);
    subscribeToUserDoc(uid);
  };

  const cleanupSingleSession = (uid?: string | null) => {
    stopHeartbeat();
    unsubscribeUserDoc();
    isActiveOwnerRef.current = false;
    sessionIdRef.current = null;
    setIsEmbargoed(false);
    hasAutoSignedOutRef.current = false;
    if (embargoTimeoutRef.current) {
      clearTimeout(embargoTimeoutRef.current);
      embargoTimeoutRef.current = null;
    }
    if (uid) {
      clearLocalSessionId(uid);
    }
  };

  // Generate a unique referral code
  const generateReferralCode = (uid: string): string => {
    // Create a referral code based on user ID and timestamp
    const timestamp = Date.now().toString(36);
    const uidPart = uid.substring(0, 6);
    return `${uidPart}${timestamp}`.toUpperCase();
  };

  // Create student user profile in Firestore
  const createStudentProfile = async (
    uid: string,
    formData: SignUpForm
  ): Promise<StudentUser> => {
    console.log("Creating student profile for UID:", uid);
    console.log("Form data:", formData);

    const now = new Date();
    
    // Handle referral logic
    let referredBy = null;
    let referralCode = generateReferralCode(uid); // Always generate a new referral code for the new user
    
    if (formData.referralCode) {
      // Find the referrer by their referral code
      try {
        const referrerQuery = query(collection(db, "users"), where("referralCode", "==", formData.referralCode));
        const referrerSnapshot = await getDocs(referrerQuery);
        
        if (!referrerSnapshot.empty) {
          const referrerDoc = referrerSnapshot.docs[0];
          referredBy = referrerDoc.id; // Set referredBy to the referrer's user ID
          console.log("User referred by:", referredBy);
        } else {
          console.warn("Referral code not found:", formData.referralCode);
        }
      } catch (error) {
        console.error("Error finding referrer:", error);
      }
    }

    const studentData: Omit<StudentUser, "uid"> = {
      email: formData.email,
      name: formData.name,
      telephone: formData.telephone || null,
      role: formData.role === "author" ? "pending" : formData.role, // Authors get 'pending' role, students keep their role
      status: formData.role === "author" ? "pending" : "Offline", // Authors get 'pending', students get 'Offline'
      program: null, // Will be set after program selection
      createdAt: now,
      updatedAt: now,
      lastLogin: now,
      profileComplete: false,
      referralCode: referralCode, // Always use the newly generated code
      referredBy: referredBy, // Set to referrer's user ID if referral code was provided
      affiliateLink: null,
      earnings: 0,
      totalrefs: 0,
      registern: formData.registern || "", // Use registration number from form (empty string if not provided)
      underef: null,
      message_id: null,
      state: "ACTIVE", // Default state for new users
    };

    console.log("Student data to save:", studentData);

    // Create user document
    console.log("Saving to Firestore collection: users, document: ", uid);
    try {
      await setDoc(doc(db, "users", uid), studentData);
      console.log("Document saved successfully");
    } catch (error) {
      console.error("Error saving document to Firestore:", error);
      throw error;
    }

    // If referral code provided, track it
    if (formData.referralCode && referredBy) {
      console.log("Tracking referral usage for code:", formData.referralCode);
      await trackReferralUsage(formData.referralCode, uid);
    }

    const result = { uid, ...studentData };
    console.log("Returning student profile:", result);
    return result;
  };

  // Track referral usage
  const trackReferralUsage = async (referralCode: string, userId: string) => {
    try {
      // Find the referrer by their referral code
      const referrerQuery = query(collection(db, "users"), where("referralCode", "==", referralCode));
      const referrerSnapshot = await getDocs(referrerQuery);
      
      if (!referrerSnapshot.empty) {
        const referrerDoc = referrerSnapshot.docs[0];
        const referrerId = referrerDoc.id;
        
        // Update referrer's total referrals count
        await updateDoc(doc(db, "users", referrerId), {
          totalrefs: increment(1),
          updatedAt: new Date()
        });
        
        console.log(`Referral tracked: User ${userId} was referred by ${referrerId}`);
      } else {
        console.warn("Referrer not found for referral code:", referralCode);
      }
    } catch (error) {
      console.error("Error tracking referral usage:", error);
    }
  };

  // Sign up new student
  const signUp = async (
    formData: SignUpForm
  ): Promise<ApiResponse<StudentUser>> => {
    try {
      console.log("Starting signup process...", formData);

      // Prevent admin role signups
      if (formData.role === "admin") {
        return { success: false, error: "Admin role is not available for public signup" };
      }

      if (formData.password !== formData.confirmPassword) {
        return { success: false, error: "Passwords do not match" };
      }

      // Create Firebase auth user
      console.log("Creating Firebase auth user...");
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      console.log("Firebase auth user created:", userCredential.user.uid);

      // Send verification email
      try {
        await sendEmailVerification(userCredential.user);
        console.log("Verification email sent");
      } catch (verifyError) {
        console.error("Error sending verification email:", verifyError);
        // We don't fail the whole signup if email fails, but we should log it
      }

      // Create student profile in Firestore
      console.log("Creating student profile in Firestore...");
      const studentProfile = await createStudentProfile(
        userCredential.user.uid,
        formData
      );
      console.log("Student profile created:", studentProfile);

      setCurrentUser(studentProfile);

      return {
        success: true,
        data: studentProfile,
        message:
          "Account created successfully! A verification email has been sent to your inbox. Please verify your email to access all features.",
      };
    } catch (error: any) {
      console.error("Signup error:", error);
      let errorMessage = "Failed to create account";

      if (error.code === "auth/email-already-in-use") {
        errorMessage = "An account with this email already exists";
      } else if (error.code === "auth/weak-password") {
        errorMessage = "Password is too weak";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Invalid email address";
      }

      return { success: false, error: errorMessage };
    }
  };

  // Sign in existing student
  const signIn = async (
    email: string,
    password: string
  ): Promise<ApiResponse<StudentUser>> => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const uid = userCredential.user.uid;

      // Get student profile from Firestore
      const studentDoc = await getDoc(doc(db, "users", uid));

      if (!studentDoc.exists()) {
        // If no student profile exists, create one
        const now = new Date();
        const student: StudentUser = {
          uid,
          email,
          name: userCredential.user.displayName || "",
          role: "student", // Default to student role
          status: "Offline", // Default status is Offline
          program: null, // No program selected yet
          createdAt: now,
          updatedAt: now,
          lastLogin: now,
          profileComplete: false,
          referralCode: null,
          referredBy: null,
          affiliateLink: null,
          earnings: 0,
          totalrefs: 0,
          registern: "", // Default empty string for registration number
          underef: null,
          message_id: null,
          state: "ACTIVE", // Default state for new users
        };

        await setDoc(doc(db, "users", uid), student);
        setCurrentUser(student);

        return {
          success: true,
          data: student,
          message: "Welcome! Please complete your profile setup.",
        };
      }

      const studentData = studentDoc.data() as Omit<StudentUser, "uid">;
      const student: StudentUser = { uid, ...studentData };

      // Update last login
      await updateDoc(doc(db, "users", uid), {
        lastLogin: new Date(),
      });

      setCurrentUser(student);

      // Initialize single session after successful sign-in
      await initializeSingleSession(uid);

      return {
        success: true,
        data: student,
        message: `Welcome back, ${student.name || "Student"}!`,
      };
    } catch (error: any) {
      let errorMessage = "Failed to sign in";

      if (error.code === "auth/user-not-found") {
        errorMessage = "No account found with this email";
      } else if (error.code === "auth/wrong-password") {
        errorMessage = "Incorrect password";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Invalid email address";
      }

      return { success: false, error: errorMessage };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      toast.success("Signed out successfully");
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Error signing out");
    }
  };

  // Update profile
  const updateProfile = async (updates: Partial<StudentUser>) => {
    if (!currentUser) return;

    try {
      const updateData = {
        ...updates,
        updatedAt: new Date(),
      };

      // If updating status to Online, also update lastLogin
      if (updates.status === "Online") {
        updateData.lastLogin = new Date();
      }

      await updateDoc(doc(db, "users", currentUser.uid), updateData);

      setCurrentUser((prev) => (prev ? { ...prev, ...updateData } : null));
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    }
  };

  // Delete account (Firestore profile, avatar in Storage, and Firebase Auth user)
  const deleteAccount = async (): Promise<void> => {
    if (!currentUser) return;
    try {
      const uid = currentUser.uid;

      // Best effort: delete avatar if present
      if (currentUser.avatar) {
        try {
          const avatarRef = ref(storage, currentUser.avatar);
          await deleteObject(avatarRef);
        } catch (err) {
          // ignore avatar deletion errors
        }
      }

      // Best effort: delete known subcollections (e.g., Courses)
      try {
        const userCoursesRef = collection(db, "users", uid, "Courses");
        const coursesSnapshot = await getFirestoreDocs(userCoursesRef);
        const deletePromises = coursesSnapshot.docs.map((d) => deleteDoc(doc(db, "users", uid, "Courses", d.id)));
        if (deletePromises.length > 0) {
          await Promise.all(deletePromises);
        }
      } catch (err) {
        // ignore subcollection deletion errors
      }

      // Delete Firestore user document
      await deleteDoc(doc(db, "users", uid));

      // Delete Firebase Auth user (may require recent login)
      if (auth.currentUser) {
        await deleteUser(auth.currentUser);
      }

      setCurrentUser(null);
      toast.success("Account deleted successfully");
    } catch (error: any) {
      console.error("Error deleting account:", error);
      if (error?.code === "auth/requires-recent-login") {
        toast.error("Please sign in again and then try deleting your account");
      } else {
        toast.error("Failed to delete account");
      }
      throw error;
    }
  };

  // Upload profile image
  const uploadProfileImage = async (file: File): Promise<string> => {
    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image size must be less than 5MB');
      }

      // Create a unique filename
      const fileExtension = file.name.split('.').pop();
      const fileName = `profile-images/${currentUser.uid}-${Date.now()}.${fileExtension}`;
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update user profile with new avatar URL
      await updateProfile({ avatar: downloadURL });
      
      toast.success("Profile image updated successfully!");
      return downloadURL;
    } catch (error) {
      console.error("Error uploading profile image:", error);
      toast.error(error instanceof Error ? error.message : "Failed to upload image");
      throw error;
    }
  };

  // Get user's enrolled courses from subcollection
  const getUserEnrolledCourses = async (): Promise<EnrolledCourse[]> => {
    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    try {
      console.log('=== DEBUG: Fetching enrolled courses for user:', currentUser.uid);
      
      // Get the Courses subcollection from the user document
      const userCoursesRef = collection(db, "users", currentUser.uid, "Courses");
      const coursesSnapshot = await getFirestoreDocs(userCoursesRef);
      
      const enrolledCourses: EnrolledCourse[] = [];
      
      coursesSnapshot.forEach((doc) => {
        const data = doc.data();
        enrolledCourses.push({
          course_id: data.course_id,
          enrolled_at: data.enrolled_at?.toDate() || new Date(),
          expire_date: data.expire_date?.toDate() || new Date(),
          status: data.status || 'active'
        });
      });
      
      console.log('=== DEBUG: Found enrolled courses:', enrolledCourses.length);
      console.log('Sample enrolled course:', enrolledCourses[0]);
      
      return enrolledCourses;
    } catch (error) {
      console.error("Error fetching enrolled courses:", error);
      toast.error("Failed to fetch enrolled courses");
      return [];
    }
  };

  // Send verification email manually
  const sendVerification = async (): Promise<ApiResponse<void>> => {
    if (!auth.currentUser) {
      return { success: false, error: "No user is currently signed in" };
    }

    try {
      await sendEmailVerification(auth.currentUser);
      return { success: true, message: "Verification email sent successfully!" };
    } catch (error: any) {
      console.error("Error sending verification email:", error);
      let errorMessage = "Failed to send verification email";
      if (error.code === "auth/too-many-requests") {
        errorMessage = "Too many requests. Please try again later.";
      }
      return { success: false, error: errorMessage };
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user: FirebaseUser | null) => {
        setFirebaseUser(user);
        if (user) {
          try {
            // Get student profile from Firestore
            const studentDoc = await getDoc(doc(db, "users", user.uid));

            if (studentDoc.exists()) {
              const studentData = studentDoc.data() as Omit<StudentUser, "uid">;
              setCurrentUser({ uid: user.uid, ...studentData });
            } else {
              // Create basic profile if none exists
              const now = new Date();
              const studentData: StudentUser = {
                uid: user.uid,
                email: user.email || "",
                name: user.displayName || "",
                role: "student", // Default to student role
                status: "Offline", // Default status is Offline
                program: null,
                createdAt: now,
                updatedAt: now,
                lastLogin: now,
                profileComplete: false,
                referralCode: null,
                referredBy: null,
                affiliateLink: null,
                earnings: 0,
                totalrefs: 0,
                registern: "", // Default empty string for registration number
                underef: null,
                message_id: null,
                state: "ACTIVE", // Default state for new users
              };

              await setDoc(doc(db, "users", user.uid), studentData);
              setCurrentUser(studentData);
            }

            // Initialize single session on auth state when signed in (e.g., page refresh)
            await initializeSingleSession(user.uid);
          } catch (error) {
            console.error("Error loading student profile:", error);
          }
        } else {
          setCurrentUser(null);
          cleanupSingleSession();
        }

        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    loading,
    signUp,
    signIn,
    logout,
    deleteAccount,
    updateProfile,
    uploadProfileImage,
    getUserEnrolledCourses,
    isAuthenticated: !!currentUser,
    firebaseUser,
    isEmbargoed,
    sendVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
